import { useState } from 'react'

const CORSTest = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const addResult = (test, success, data, error = null) => {
    setResults(prev => [...prev, {
      test,
      success,
      data,
      error,
      timestamp: new Date().toLocaleString()
    }])
  }

  const testCORS = async () => {
    setLoading(true)
    setResults([])

    // Test 1: Simple GET request
    try {
      console.log('Testing GET /api/health...')
      const response = await fetch('http://localhost:5000/api/health', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      const data = await response.json()
      addResult('GET /api/health', true, JSON.stringify(data, null, 2))
    } catch (error) {
      console.error('GET /api/health failed:', error)
      addResult('GET /api/health', false, null, error.message)
    }

    // Test 2: CORS test endpoint
    try {
      console.log('Testing GET /api/cors-test...')
      const response = await fetch('http://localhost:5000/api/cors-test', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      const data = await response.json()
      addResult('GET /api/cors-test', true, JSON.stringify(data, null, 2))
    } catch (error) {
      console.error('GET /api/cors-test failed:', error)
      addResult('GET /api/cors-test', false, null, error.message)
    }

    // Test 3: POST to login
    try {
      console.log('Testing POST /api/auth/login...')
      const response = await fetch('http://localhost:5000/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: 'admin',
          password: 'admin123'
        })
      })
      
      const data = await response.json()
      addResult('POST /api/auth/login', response.ok, JSON.stringify(data, null, 2))
    } catch (error) {
      console.error('POST /api/auth/login failed:', error)
      addResult('POST /api/auth/login', false, null, error.message)
    }

    // Test 4: POST to register
    try {
      console.log('Testing POST /api/auth/register...')
      const testUser = {
        username: 'testuser_' + Date.now(),
        email: 'test_' + Date.now() + '@example.com',
        password: 'test123',
        full_name: 'Test User'
      }
      
      const response = await fetch('http://localhost:5000/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testUser)
      })
      
      const data = await response.json()
      addResult('POST /api/auth/register', response.ok, JSON.stringify(data, null, 2))
    } catch (error) {
      console.error('POST /api/auth/register failed:', error)
      addResult('POST /api/auth/register', false, null, error.message)
    }

    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">🔍 CORS Debugging Tool</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Backend Connection Tests</h2>
          <p className="text-gray-600 mb-4">
            This tool tests the connection between frontend (http://localhost:3000) and backend (http://localhost:5000)
          </p>
          
          <button 
            onClick={testCORS}
            disabled={loading}
            className={`px-6 py-3 rounded-lg font-semibold ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600'
            } text-white transition-colors`}
          >
            {loading ? '🔄 Testing...' : '🚀 Run CORS Tests'}
          </button>

          <button 
            onClick={() => setResults([])}
            className="ml-4 px-6 py-3 rounded-lg font-semibold bg-gray-500 hover:bg-gray-600 text-white transition-colors"
          >
            🗑️ Clear Results
          </button>
        </div>

        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">📊 Test Results</h2>
            <div className="space-y-4">
              {results.map((result, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  result.success 
                    ? 'border-green-400 bg-green-50' 
                    : 'border-red-400 bg-red-50'
                }`}>
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg">{result.test}</h3>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      result.success 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {result.success ? '✅ SUCCESS' : '❌ FAILED'}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2">Time: {result.timestamp}</p>
                  
                  {result.error && (
                    <div className="mb-2">
                      <p className="font-medium text-red-700">Error:</p>
                      <p className="text-red-600 bg-red-100 p-2 rounded">{result.error}</p>
                    </div>
                  )}
                  
                  {result.data && (
                    <div>
                      <p className="font-medium text-green-700">Response:</p>
                      <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto max-h-48 overflow-y-auto">
                        {result.data}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2">💡 Debugging Tips:</h3>
          <ul className="text-blue-700 space-y-1 text-sm">
            <li>• If all tests fail with "Failed to fetch" - Backend server is not running</li>
            <li>• If tests fail with CORS errors - CORS headers are not properly configured</li>
            <li>• If GET works but POST fails - Preflight handling issue</li>
            <li>• Check browser console and backend terminal for detailed error logs</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default CORSTest